// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { ago } = require('shared/date')
const { formatCount, pluralize } = require('shared/string-utils')
// Client V2 common imports
const {
  CircleButton,
  CollectionSubscribeCircleButton
} = require('client-v2/common/components/buttons')
const LazyImage = require('client-v2/common/components/lazy-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const Icon = require('client-v2/common/containers/icons/component')
const { usePropsEventCallback } = require('client-v2/common/hooks/callbacks')
const collectionModelActions = require('client-v2/common/models/collection/actions')
const CollectionModelRecord = require('client-v2/common/models/collection/record')
const { stopPropagation } = require('client-v2/common/utils/events')
const tag = require('client-v2/common/utils/hyperscript')

const posts = pluralize('post')
const subscribers = pluralize('subscriber')

// BEM entity block definition

const bem = BemEntity('CollectionCard')

function CollectionCard(props) {
  const onCardClick = usePropsEventCallback(props, 'onCardClick')
  return (
    tag('div', {
      className: classNames(
        props.className,
        bem.block({
          'has-rec-reason': !!props.recReason,
          'hrefs-enabled': props.isHrefsEnabled,
          interactive: !!props.onCardClick,
          // Experimental flags for improving top-details readability
          'text-shadow': props.isTextShadowEnabled,
          'text-stroke': props.isTextStrokeEnabled
        })
      ),
      dataset: {
        mixId: props.collection.id
      }
    },
      tag('div', {
        className: bem.element('inner'),
        onClick: onCardClick
      },
        tag(CollectionCardCoverImage, props),
        tag(CollectionCardTopDetails, props),
        tag(CollectionCardBottomDetails, props),
        tag(CollectionCardButtons, props)
      )
    )
  )
}

CollectionCard.defaultProps = {
  // Flags
  isHrefsEnabled: true,
  isPostCountVisible: true,
  isSubscriberCountVisible: true
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')
  const { RecReasonRecord } = require('client-v2/common/containers/feed/records')
  const PageContextRecord = require('client-v2/common/models/page-context/record')

  CollectionCard.propTypes = {
    // Flags
    isHrefsEnabled: PropTypes.bool,
    isPostCountVisible: PropTypes.bool,
    isSelf: PropTypes.bool,
    isSubscriberCountVisible: PropTypes.bool,
    isTextShadowEnabled: PropTypes.bool,
    isTextStrokeEnabled: PropTypes.bool,
    // Values
    className: PropTypes.string,
    collection: PropTypes.shape(CollectionModelRecord.propTypes).isRequired,
    pageContext: PropTypes.shape(PageContextRecord.propTypes),
    recReason: PropTypes.shape(RecReasonRecord.propTypes),
    // Callbacks
    onCardClick: PropTypes.func,
    onPostCountClick: PropTypes.func,
    onShareClick: PropTypes.func,
    onSubscribeClick: PropTypes.func,
    onSubscriberCountClick: PropTypes.func,
    onUserClick: PropTypes.func
  }
}

module.exports = React.memo(CollectionCard)

// BEM Block entity Element components

function CollectionCardCoverImage(props) {
  const { collection } = props
  let coverImageUrl = props['cover_image_url']
  if (collection['cover_image_urls']) {
    coverImageUrl = collection['cover_image_urls'].large
  }
  return (
    tag('div', {
      className: bem.element('image-container')
    },
      tag(LazyImage, {
        className: bem.element('image'),
        sources: [ coverImageUrl, ...collection.images ]
      }),
      tag('div', {
        className: bem.element('image-overlay')
      })
    )
  )
}

function CollectionCardTopDetails(props) {
  return (
    tag('div', {
      className: bem.element('top-details')
    },
      tag(CollectionCardPostCount, props),
      tag(CollectionCardVisibility, props),
      tag(CollectionCardName, props)
    )
  )
}

function CollectionCardBottomDetails(props) {
  return (
    tag('div', {
      className: bem.element('bottom-details')
    },
      tag(CollectionCardCreatorAvatar, props),
      tag(CollectionCardCreator, props),
      tag(CollectionCardLastUpdated, props),
      tag(CollectionCardRecReason, props)
    )
  )
}

function CollectionCardButtons(props) {
  return (
    tag('div', {
      className: bem.element('buttons')
    },
      tag(CollectionCardSubscriberCount, props),
      tag(CollectionCardSubscribeButton, props),
      tag(CollectionCardShareButton, props)
    )
  )
}

function CollectionCardVisibility(props) {
  const { visibility } = props.collection
  const isPrivate = visibility === CollectionModelRecord.VISIBILITY_PRIVATE
  return (
    tag('div', {
      className: bem.element('visibility', {
        private: isPrivate
      })
    },
      isPrivate && 'Private ',
      'Collection'
    )
  )
}

function CollectionCardName(props) {
  return (
    tag('a', {
      className: bem.element('name'),
      href: collectionHref(props)
    },
      props.collection.name
    )
  )
}

function CollectionCardPostCount(props) {
  const onClick = usePropsEventCallback(props, 'onPostCountClick')
  const count = props.collection['posts_count']
  if (!props.isPostCountVisible || !count) {
    return null
  }
  return (
    tag('a', {
      className: bem.element('post-count'),
      href: collectionPostsHref(props),
      title: `This collection has ${count} ${posts(count)}`,
      onClick
    },
      formatCount(count)
    )
  )
}

function CollectionCardShareButton(props) {
  const onClick = usePropsEventCallback(props, 'onShareClick')
  if (!props.onShareClick) {
    return null
  }
  return (
    tag(CircleButton, {
      className: bem.element('share-button'),
      onClick
    },
      tag(Icon, { id: 'share-network' })
    )
  )
}

function CollectionCardSubscribeButton(props) {
  const onClick = usePropsEventCallback(props, 'onSubscribeClick')
  if (props.isSelf || !props.onSubscribeClick) {
    return null
  }
  return (
    tag(CollectionSubscribeCircleButton, {
      className: bem.element('subscribe-button'),
      checked: props.collection.isFollowing,
      onClick
    })
  )
}

function CollectionCardSubscriberCount(props) {
  const onClick = usePropsEventCallback(props, 'onSubscriberCountClick')
  const count = props.collection['follower_count']
  if (!props.isSubscriberCountVisible || !count) {
    return null
  }
  return (
    tag('a', {
      className: bem.element('subscriber-count'),
      href: collectionSubscribersHref(props),
      title: `This collection has ${count} ${subscribers(count)}`,
      onClick
    },
      `${formatCount(count)}`
    )
  )
}

function CollectionCardCreatorAvatar(props) {
  const onClick = usePropsEventCallback(props, 'onUserClick')
  return (
    tag(UserAvatar, {
      className: bem.element('creator-avatar'),
      href: true,
      user: props.collection,
      onClick
    })
  )
}

function CollectionCardCreator(props) {
  const onClick = usePropsEventCallback(props, 'onUserClick')
  return (
    tag('a', {
      className: bem.element('creator-href'),
      href: userHref(props),
      onClick
    },
      tag('span', {
        className: bem.element('creator-label')
      },
        `Created by `
      ),
      tag('span', {
        className: bem.element('creator-name')
      },
        props.isSelf
        ? 'you'
        : `@${props.collection.username}`
      )
    )
  )
}

function CollectionCardLastUpdated(props) {
  const modifiedAtString = props.collection['modified_at']
  const modifiedAtDate = React.useMemo(
    () => modifiedAtString ? new Date(modifiedAtString) : null,
    [ modifiedAtString ]
  )
  if (!modifiedAtDate) {
    return null
  }
  return (
    tag('p', {
      className: bem.element('last-updated')
    },
      tag('span', {
        className: bem.element('last-updated-label')
      },
        'Updated '
      ),
      tag('time', {
        className: bem.element('last-updated-time'),
        dateTime: modifiedAtDate.toISOString()
      },
        ago(modifiedAtDate)
      )
    )
  )
}

function CollectionCardRecReason(props) {
  const { html: recReasonHtml } = props.recReason || {}
  if (!recReasonHtml) {
    return null
  }
  return (
    tag('div', {
      className: bem.element('rec-reason'),
      dangerouslySetInnerHTML: { __html: recReasonHtml },
      onClick: stopPropagation
    })
  )
}

// Linkage helpers

function collectionHref(props) {
  if (props.isHrefsEnabled) {
    return collectionModelActions.collectionProfileHref(props.collection)
  }
}

function collectionPostsHref(props) {
  if (props.isHrefsEnabled) {
    return collectionModelActions.collectionProfilePostsHref(props.collection)
  }
}

function collectionSubscribersHref(props) {
  if (props.isHrefsEnabled) {
    return collectionModelActions.collectionProfileSubscribersHref(props.collection)
  }
}

function userHref(props) {
  if (props.isHrefsEnabled) {
    return collectionModelActions.collectionUserProfileHref(props.collection)
  }
}
